import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  useTheme,
  Stack,
  Paper,
  Chip,
} from "@mui/material";
import {
  faRocket,
  faGraduationCap,
  faHandshake,
  faHeart,
  faAward,
  faCalendarAlt,
  faMapMarkerAlt,
  faBriefcase,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Helmet } from "react-helmet-async";

export const Career = () => {
  const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const benefits = [
    {
      icon: faGraduationCap,
      title: "Desenvolvimento Profissional",
      description:
        "Programas de treinamento contínuo e oportunidades de certificação",
    },
    {
      icon: faRocket,
      title: "Plano de Carreira",
      description:
        "Trajetória clara de crescimento e promoções baseadas em mérito",
    },
    {
      icon: faCalendarAlt,
      title: "Flexibilidade",
      description:
        "Horários flexíveis e possibilidade de trabalho remoto quando aplicável",
    },
    {
      icon: faHeart,
      title: "Saúde e Bem-estar",
      description: "Plano de saúde completo e programas de qualidade de vida",
    },
    {
      icon: faAward,
      title: "Reconhecimento",
      description: "Programas de bonificação e reconhecimento por desempenho",
    },
    {
      icon: faHandshake,
      title: "Ambiente Colaborativo",
      description: "Cultura de trabalho em equipe e suporte mútuo",
    },
  ];

  interface Position {
    title: string;
    department: string;
    location: string;
    type: string;
    level: string;
    description: string;
  }

  const openPositions: Position[] = [
    // {
    //   title: "Técnico em Climatização",
    //   department: "Instalação e Manutenção",
    //   location: "Luanda",
    //   type: "Tempo Integral",
    //   level: "Júnior/Pleno",
    //   description:
    //     "Instalação, manutenção e reparação de sistemas de ar condicionado residenciais e comerciais.",
    // },
    // {
    //   title: "Engenheiro Eletrotécnico",
    //   department: "Projetos Elétricos",
    //   location: "Luanda",
    //   type: "Tempo Integral",
    //   level: "Pleno/Sênior",
    //   description:
    //     "Desenvolvimento de projetos elétricos prediais e industriais, acompanhamento de obras.",
    // },
    // {
    //   title: "Supervisor de Obras",
    //   department: "Construção Civil",
    //   location: "Benguela",
    //   type: "Tempo Integral",
    //   level: "Pleno",
    //   description:
    //     "Coordenação de equipes de construção civil e garantia de qualidade em obras.",
    // },
    // {
    //   title: "Técnico em Hidráulica",
    //   department: "Instalações Hidráulicas",
    //   location: "Huambo",
    //   type: "Tempo Integral",
    //   level: "Júnior",
    //   description:
    //     "Instalação e manutenção de sistemas hidráulicos residenciais e comerciais.",
    // },
  ];

  const values = [
    "Compromisso com a Excelência",
    "Inovação Constante",
    "Trabalho em Equipe",
    "Integridade e Transparência",
    "Foco no Cliente",
    "Responsabilidade Social",
  ];

  return (
    <>
      <Helmet>
        <title>Carreiras e Oportunidades | Clima Tropical Angola</title>

        <meta
          name="description"
          content="Trabalhe na Clima Tropical em Angola. Descubra oportunidades de carreira em climatização, construção civil, serviços elétricos e áreas técnicas especializadas."
        />

        <meta
          name="keywords"
          content="carreiras Angola, vagas climatização, empregos construção civil Angola, recrutamento Luanda, oportunidades profissionais Angola, Clima Tropical empregos"
        />

        <link rel="canonical" href="https://climatropical.com/careers" />

        {/* Open Graph */}
        <meta
          property="og:title"
          content="Carreiras e Oportunidades | Clima Tropical Angola"
        />

        <meta
          property="og:description"
          content="Faça parte da Clima Tropical e construa uma carreira em uma empresa referência em climatização e serviços especializados em Angola."
        />

        <meta property="og:url" content="https://climatropical.com/careers" />

        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta
          name="twitter:title"
          content="Carreiras e Oportunidades | Clima Tropical Angola"
        />

        <meta
          name="twitter:description"
          content="Veja oportunidades de emprego e crescimento profissional na Clima Tropical em Angola."
        />
      </Helmet>
      <Box
        sx={{
          backgroundColor: theme.palette.background.default,
          minHeight: "100vh",
          pt: 0,
        }}
      >
        {/* Hero Section */}
        <Box
          sx={{
            background: `linear-gradient(130deg, ${theme.palette.primary.main} 30%, ${theme.palette.info.main} 170%)`,
            color: "white",
            py: { xs: 8, md: 12 },
            mb: 8,
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ maxWidth: "100%", mx: "auto" }}>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: "bold",
                  mb: 3,
                  color: theme.palette.info.light,
                }}
              >
                Faça Parte do Nosso Time
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  opacity: 0.9,
                  lineHeight: 1.6,
                }}
              >
                Junte-se à Clima Tropical e construa uma carreira com propósito,
                inovação e oportunidades reais de crescimento
              </Typography>
              <Button
                variant="contained"
                size="large"
                sx={{
                  backgroundColor: "white",
                  color: theme.palette.primary.main,
                  fontWeight: "bold",
                  px: 3,
                  py: 1,
                  fontSize: "1.1rem",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    transform: "translateY(-2px)",
                  },
                  transition: "all 0.3s ease",
                }}
                href="#vagas"
              >
                Ver Vagas Abertas
              </Button>
            </Box>
          </Container>
        </Box>

        <Container maxWidth="lg">
          {/* Why Work With Us Section */}
          <Box sx={{ mb: { xs: 8, md: 12 } }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: "center",
                gap: 6,
              }}
            >
              <Box sx={{ width: { xs: "100%", md: "50%" } }}>
                <Typography
                  variant="h4"
                  component="h2"
                  gutterBottom
                  sx={{
                    fontWeight: "bold",
                    color: theme.palette.primary.main,
                    mb: 4,
                  }}
                >
                  Por que Trabalhar na Clima Tropical?
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.text.secondary,
                    lineHeight: 1.8,
                    fontSize: "1.1rem",
                    mb: 4,
                  }}
                >
                  Na Clima Tropical, não oferecemos apenas empregos -
                  construímos carreiras. Desde 2018, temos orgulho de ser uma
                  empresa onde talentos florescem, ideias são valorizadas e o
                  crescimento profissional é uma prioridade.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.text.secondary,
                    lineHeight: 1.8,
                    fontSize: "1.1rem",
                  }}
                >
                  Somos mais do que uma empresa de climatização e construção
                  civil. Somos uma família de profissionais apaixonados por
                  transformar o Angola através de projetos inovadores e serviços
                  de excelência.
                </Typography>
              </Box>

              <Box sx={{ width: { xs: "100%", md: "50%" } }}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 4,
                    backgroundColor: theme.palette.primary.dark,
                    color: "white",
                    borderRadius: 4,
                  }}
                >
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{
                      fontWeight: "bold",
                      mb: 3,
                      color: theme.palette.info.light,
                    }}
                  >
                    Nossos Valores
                  </Typography>
                  <Stack spacing={2}>
                    {values.map((value, index) => (
                      <Box
                        key={index}
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            backgroundColor: "white",
                            borderRadius: "50%",
                          }}
                        />
                        <Typography>{value}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              </Box>
            </Box>
          </Box>

          {/* Benefits Section */}
          <Box sx={{ mb: { xs: 12, md: 12 } }}>
            <Typography
              variant="h4"
              component="h2"
              gutterBottom
              sx={{
                fontWeight: "bold",
                textAlign: "center",
                color: theme.palette.primary.main,
                mb: 2,
              }}
            >
              Benefícios e Vantagens
            </Typography>
            <Typography
              variant="h6"
              sx={{
                textAlign: "center",
                color: theme.palette.text.secondary,
                mb: 6,
                maxWidth: "600px",
                mx: "auto",
              }}
            >
              Investimos no seu sucesso e bem-estar com um pacote completo de
              benefícios
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(2, 1fr)",
                  lg: "repeat(3, 1fr)",
                },
                gap: 4,
              }}
            >
              {benefits.map((benefit, index) => (
                <Card
                  key={index}
                  sx={{
                    width: {
                      xs: "100%",
                      sm: "calc(50% - 16px)",
                      md: "100%",
                    },
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: theme.shadows[8],
                    },
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: "center" }}>
                    <Box
                      sx={{
                        backgroundColor: theme.palette.primary.main,
                        color: "white",
                        borderRadius: "50%",
                        width: 70,
                        height: 70,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 3,
                      }}
                    >
                      <FontAwesomeIcon icon={benefit.icon} size="xl" />
                    </Box>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        fontWeight: "bold",
                        color: theme.palette.primary.main,
                        mb: 2,
                      }}
                    >
                      {benefit.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        lineHeight: 1.6,
                      }}
                    >
                      {benefit.description}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>

          {/* Open Positions Section */}
          <Box id="vagas">
            <Typography
              variant="h4"
              component="h2"
              gutterBottom
              sx={{
                fontWeight: "bold",
                textAlign: "center",
                color: theme.palette.primary.main,
                mb: 2,
              }}
            >
              Vagas em Destaque
            </Typography>
            <Typography
              variant="h6"
              sx={{
                textAlign: "center",
                color: theme.palette.text.secondary,
                mb: 6,
                maxWidth: "600px",
                mx: "auto",
              }}
            >
              Encontre a oportunidade perfeita para sua carreira
            </Typography>

            <Stack spacing={3} sx={{ mb: { xs: 8, md: 8 } }}>
              {openPositions.length === 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 4, md: 6 },
                    textAlign: "center",
                    backgroundColor: theme.palette.action.hover,
                    borderRadius: 2,
                    border: `2px dashed ${theme.palette.divider}`,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    mx: "auto",
                  }}
                >
                  {/* Ícone de maleta aberta/vazia usando FontAwesome */}
                  <Box
                    sx={{
                      fontSize: "3rem",
                      color: theme.palette.text.disabled,
                      mb: 2,
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faBriefcase}
                      style={{ opacity: 0.6 }}
                    />
                  </Box>

                  <Typography
                    variant="h5"
                    component="h3"
                    gutterBottom
                    sx={{
                      fontWeight: "bold",
                      color: theme.palette.text.primary,
                    }}
                  >
                    Nenhuma vaga aberta no momento
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.text.secondary,
                      mb: 4,
                      maxWidth: "500px",
                      lineHeight: 1.6,
                    }}
                  >
                    Não encontrou o que procurava? Não se preocupe! Cadastre o
                    seu currículo no nosso **Banco de Talentos** para que
                    possamos considerar o seu perfil em futuras oportunidades.
                  </Typography>

                  <Button
                    variant="outlined"
                    color="primary"
                    size="large"
                    startIcon={<FontAwesomeIcon icon={faBriefcase} />} // Ou outro ícone de envio/upload
                    sx={{
                      fontWeight: "bold",
                      px: 4,
                      py: 1.5,
                      borderRadius: "8px",
                      borderWidth: "2px",
                      "&:hover": {
                        borderWidth: "2px",
                      },
                    }}
                    onClick={() => {
                      // Aqui você pode abrir um modal, scrollar para o formulário de contato
                      // ou disparar um 'mailto:rh@empresa.com'
                      window.location.href =
                        "mailto:recrutamento@empresa.com?subject=Banco de Talentos";
                    }}
                  >
                    Enviar Currículo para o Banco de Talentos
                  </Button>
                </Paper>
              )}

              {openPositions.map((position, index) => (
                <Paper
                  key={index}
                  elevation={2}
                  sx={{
                    p: 4,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: theme.shadows[6],
                      borderLeft: `4px solid ${theme.palette.primary.main}`,
                    },
                    borderLeft: `4px solid transparent`,
                  }}
                >
                  {/* ... manter o resto do mapeamento igual ... */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", md: "row" },
                      justifyContent: "space-between",
                      alignItems: { xs: "flex-start", md: "center" },
                      gap: 3,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h5"
                        gutterBottom
                        sx={{
                          fontWeight: "bold",
                          color: theme.palette.primary.main,
                        }}
                      >
                        {position.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ color: theme.palette.text.secondary, mb: 2 }}
                      >
                        {position.description}
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        <Chip
                          icon={<FontAwesomeIcon icon={faBriefcase} />}
                          label={position.department}
                          variant="outlined"
                          size="small"
                        />
                        <Chip
                          icon={<FontAwesomeIcon icon={faMapMarkerAlt} />}
                          label={position.location}
                          variant="outlined"
                          size="small"
                        />
                        <Chip
                          label={position.type}
                          variant="outlined"
                          size="small"
                        />
                        <Chip
                          label={position.level}
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                    </Box>
                    <Button
                      variant="contained"
                      size="large"
                      sx={{
                        fontWeight: "bold",
                        px: 4,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Candidatar-se
                    </Button>
                  </Box>
                </Paper>
              ))}
            </Stack>
          </Box>

          {/* CTA Section */}
        </Container>
      </Box>
    </>
  );
};
